import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { waeAPI, STATUS_STYLE, STATUS_LABEL, fmtDT } from "./waeApi";
import { Search, HistoryIcon, History as HistoryLucide } from "lucide-react";
import { EmptyState } from "./uiKit";
import RequestDrawer from "./RequestDrawer";

const TABS = [
  { k: "",           label: "All" },
  { k: "approved",   label: "Approved" },
  { k: "rejected",   label: "Rejected" },
  { k: "cancelled",  label: "Cancelled" },
];

export default function WAEHistory() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [openId, setOpenId] = useState(null);

  const load = () => waeAPI.history({ status: status || undefined, limit: 500 })
                        .then(setRows).catch(()=>{});
  useEffect(() => { load(); }, [status]);  // eslint-disable-line

  const filtered = useMemo(() =>
    q ? rows.filter(r =>
      (r.code||"").toLowerCase().includes(q.toLowerCase()) ||
      (r.subject||"").toLowerCase().includes(q.toLowerCase()) ||
      (r.requester_email||"").toLowerCase().includes(q.toLowerCase())
    ) : rows
  , [rows, q]);

  return (
    <div className="space-y-4" data-testid="wae-history">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2"/>
          <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Search history…" aria-label="Search history"
                 data-testid="wae-hist-search"
                 className="h-10 pl-9 pr-3 rounded-lg border border-slate-200 bg-white text-[13px] w-72 focus:border-hocp-navy outline-none"/>
        </div>
        <div className="flex gap-1" data-testid="wae-hist-tabs">
          {TABS.map(t => (
            <button key={t.k} onClick={()=>setStatus(t.k)}
                    data-testid={`wae-hist-tab-${t.k || "all"}`}
                    className={`h-9 px-3 rounded-lg text-[12.5px] font-medium border ${
                      status===t.k ? "bg-hocp-mint text-hocp-deepTeal border-hocp-mint" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
              {t.label}
            </button>
          ))}
        </div>
        <div className="ml-auto text-[12px] text-slate-500 mono">{filtered.length} finalised requests</div>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        {filtered.length === 0
          ? <EmptyState icon={HistoryLucide} title="No history yet"
              msg="Completed and rejected requests will appear here."/>
          : (
            <table className="w-full text-[13px]">
              <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-3">Code</th>
                  <th className="text-left px-4 py-3">Subject</th>
                  <th className="text-left px-4 py-3">Type</th>
                  <th className="text-left px-4 py-3">Requester</th>
                  <th className="text-right px-4 py-3">Amount</th>
                  <th className="text-center px-4 py-3">Status</th>
                  <th className="text-left px-4 py-3">Finalised</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map(r => (
                  <tr key={r.id} onClick={()=>setOpenId(r.id)} data-testid={`wae-hist-row-${r.id}`}
                      className="cursor-pointer hover:bg-slate-50/50">
                    <td className="px-4 py-3 mono font-medium text-hocp-navy">{r.code}</td>
                    <td className="px-4 py-3 max-w-xs truncate">{r.subject}</td>
                    <td className="px-4 py-3 text-[12px] text-slate-600">{r.approval_type_name}</td>
                    <td className="px-4 py-3 text-[12px] text-slate-600 truncate max-w-[160px]">{r.requester_email}</td>
                    <td className="px-4 py-3 text-right tabular-nums font-medium">
                      {r.amount != null ? `${r.currency||"INR"} ${Number(r.amount).toLocaleString("en-IN")}` : "—"}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`px-2 py-0.5 rounded-md text-[11px] font-medium border ${STATUS_STYLE[r.status] || ""}`}>
                        {STATUS_LABEL[r.status] || r.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-[12px] text-slate-500 mono">{fmtDT(r.completed_at || r.rejected_at || r.cancelled_at || r.updated_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </div>

      {openId && <RequestDrawer requestId={openId} currentUserId={user?.id || user?._id}
                                  onClose={()=>setOpenId(null)} onChanged={load}/>}
    </div>
  );
}
